import { formatMoney, formatPercent } from "./calc.js";
import { openHtmlDocument } from "./docOpen.js";
import { derived, taxManualPrintNote } from "./state.js";

const BRAND = "Desking by EnvisionDMS.com";

function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function vehicleLine(c) {
  const base = [c.year, c.make, c.model].filter(Boolean).join(" ") || "Vehicle";
  const extras = [];
  if (String(c.color || "").trim()) extras.push(`Color ${String(c.color).trim()}`);
  if (String(c.body || "").trim()) extras.push(`Body ${String(c.body).trim()}`);
  if (String(c.mileage || "").trim()) {
    extras.push(`Odometer ${String(c.mileage).trim()} mi`);
  }
  return extras.length ? `${base} · ${extras.join(" · ")}` : base;
}

/** Normalize print opts with sensible defaults. */
function normalizeOpts(opts = {}) {
  return {
    version: opts.version === "dealership" ? "dealership" : "customer",
    showCost: opts.showCost !== false,
    showFrontGross: opts.showFrontGross !== false,
    showBackGross: opts.showBackGross !== false,
    showNetProfit: opts.showNetProfit !== false,
    showTradeCredit: opts.showTradeCredit !== false,
    showApr: opts.showApr !== false,
    showFinanceCharge: opts.showFinanceCharge !== false,
    showAmountFinanced: opts.showAmountFinanced !== false,
    showDealStructure: opts.showDealStructure !== false,
    template: opts.template === "grid" ? "grid" : "classic",
  };
}

function printChrome(css) {
  return `
    @page { size: letter; margin: 0.5in; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: "Source Serif 4", Georgia, serif;
      color: #1d2c26;
      background: #fff;
    }
    .page {
      max-width: 7.5in;
      margin: 0 auto;
      min-height: 0;
      display: block;
      padding: 0 0 0.15in;
    }
    .page-body { display: block; }
    .page-foot {
      display: block;
      margin-top: 10px;
      padding-top: 6px;
      background: #fff;
    }
    .hdr {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 8px;
      border-bottom: 2px solid #3d8f6e;
      margin-bottom: 6px;
      background: linear-gradient(120deg, rgba(111,191,154,0.12), rgba(255,255,255,0));
      border-radius: 4px 4px 0 0;
      padding: 5px 8px;
    }
    .hdr h1 {
      font-family: Fraunces, Georgia, serif;
      font-size: 15px;
      margin: 0;
      letter-spacing: 0.04em;
      color: #2f6b52;
    }
    .hdr .tag {
      font-family: "DM Sans", sans-serif;
      font-size: 8.5px;
      color: #3f6554;
      margin-top: 1px;
    }
    .hdr .side {
      text-align: right;
      font-size: 8.5px;
      color: #456356;
      font-family: "DM Sans", sans-serif;
      line-height: 1.25;
      max-width: 58%;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    .hdr .side .muted { margin: 0; font-size: 8px; }
    h2 {
      font-family: "DM Sans", sans-serif;
      font-size: 11px;
      margin: 0 0 4px;
      color: #243f33;
    }
    h3 {
      font-family: "DM Sans", sans-serif;
      font-size: 9.5px;
      margin: 5px 0 3px;
      color: #2f6b52;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    table.lines { width: 100%; border-collapse: collapse; }
    table.lines th, table.lines td {
      padding: 2px 3px;
      border-bottom: 1px solid #d9e9e0;
      font-size: 9.5px;
    }
    table.lines th { text-align: left; color: #3d5c4d; font-weight: 600; width: 55%; }
    table.lines td { text-align: right; font-variant-numeric: tabular-nums; }
    .hero-pay {
      display: flex;
      justify-content: space-between;
      gap: 6px;
      margin: 4px 0 6px;
      padding: 5px 8px;
      border-radius: 6px;
      background: linear-gradient(135deg, #e8f7ef, #f7fcf9);
      border: 1px solid #c5e2d3;
    }
    .hero-pay div { font-family: "DM Sans", sans-serif; }
    .hero-pay strong {
      display: block;
      font-family: Fraunces, Georgia, serif;
      font-size: 14px;
      color: #2f6b52;
      margin-top: 1px;
    }
    .hero-pay span { font-size: 8px; text-transform: uppercase; letter-spacing: 0.06em; color: #4d6b5c; }
    .quote-classic .hero-pay strong { font-size: 15px; }
    .select-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 3px 12px;
      align-items: baseline;
      margin: 0 0 6px;
      padding: 5px 8px;
      border-radius: 6px;
      background: #2f6b52;
      color: #fff;
      font-family: "DM Sans", sans-serif;
      font-size: 9.5px;
    }
    .select-bar strong {
      font-family: Fraunces, Georgia, serif;
      font-size: 14px;
      font-weight: 700;
    }
    .select-bar span { opacity: 0.92; }
    table.pay-matrix {
      width: 100%;
      border-collapse: collapse;
      margin: 0 0 5px;
      font-family: "DM Sans", sans-serif;
      font-size: 9px;
      table-layout: fixed;
    }
    table.pay-matrix th,
    table.pay-matrix td {
      border: 1px solid #c5e2d3;
      padding: 3px 2px;
      text-align: center;
      vertical-align: middle;
    }
    table.pay-matrix thead th {
      background: #e8f7ef;
      color: #2f6b52;
      font-size: 7.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
    table.pay-matrix th.term-cell {
      text-align: left;
      background: #f7fcf9;
      color: #243f33;
      font-weight: 600;
      white-space: nowrap;
      font-size: 8px;
      padding-left: 4px;
    }
    table.pay-matrix td.pay-cell {
      font-variant-numeric: tabular-nums;
      font-weight: 600;
      color: #243f33;
      background: #fff;
    }
    table.pay-matrix td.pay-cell.is-selected {
      background: #e8f7ef;
      color: #2f6b52;
      border-color: #3d8f6e;
      box-shadow: inset 0 0 0 1px #3d8f6e;
      font-weight: 700;
    }
    .note {
      margin: 3px 0 0;
      font-size: 7.5px;
      line-height: 1.25;
      color: #435e51;
      border-top: 1px solid #d9e9e0;
      padding-top: 3px;
    }
    .sig-block {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px 14px;
    }
    .sig-block .line {
      margin: 0;
      padding: 0;
      border: 0;
      min-height: 0;
      font-family: "DM Sans", sans-serif;
      color: #243f33;
    }
    .sig-block .sig-space { height: 28px; }
    .sig-block .sig-rule { border-top: 1px solid #1d2c26; }
    .sig-block .sig-label {
      font-size: 8px;
      padding-top: 2px;
      line-height: 1.2;
    }
    .dealer-only { margin-top: 3px; }
    .dealer-only h3 {
      font-family: "DM Sans", sans-serif;
      font-size: 8.5px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #3d8f6e;
      margin: 0 0 2px;
    }
    .muted { color: #6a8075; font-size: 8.5px; margin: 2px 0; }
    /* Screen preview: pin foot to bottom of a letter-tall canvas when short. */
    @media screen {
      .page {
        min-height: calc(11in - 1.3in);
        display: flex;
        flex-direction: column;
      }
      .page-foot { margin-top: auto; }
    }
    /*
     * Print: fixed page footer repeats on EVERY printed page near the bottom.
     * Content stays in normal flow — no full-page flex pin (avoids blank pages).
     * Bottom @page margin reserves room so content does not collide with foot.
     */
    @media print {
      html, body { height: auto !important; overflow: visible !important; }
      .page {
        max-width: none;
        width: 100%;
        min-height: 0 !important;
        height: auto !important;
        display: block !important;
        padding: 0 !important;
      }
      .page-body {
        display: block !important;
        /* Reserve vertical room so flowing content never sits under the fixed foot. */
        padding-bottom: 0.95in !important;
      }
      .page-foot {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        margin: 0 !important;
        padding: 6px 0 0 !important;
        background: #fff !important;
        z-index: 10;
      }
      .sig-block .sig-space { height: 22px; }
      .quote-grid > h2,
      .quote-classic > h2 { display: none; }
    }
    ${css || ""}
  `;
}

function wrapDocument(title, inner) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(title)}</title>
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&display=swap" rel="stylesheet" />
  <style>${printChrome()}</style>
</head>
<body>
  <div class="page">${inner}</div>
  <script>
  (function () {
    function whenReady(fn) {
      var run = function () {
        var fontsReady = (document.fonts && document.fonts.ready)
          ? document.fonts.ready.catch(function () {})
          : Promise.resolve();
        fontsReady.then(function () {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              setTimeout(fn, 40);
            });
          });
        });
      };
      if (document.readyState === "complete") run();
      else window.addEventListener("load", run);
    }
    whenReady(function () { window.focus(); window.print(); });
  })();
  </script>
</body>
</html>`;
}

function headerBlock(state, d, label) {
  const c = state.customer;
  const vehicle = vehicleLine(c);
  return `
    <div class="hdr">
      <div>
        <h1>ENVISION</h1>
        <div class="tag">${escapeHtml(BRAND)} · ${escapeHtml(label)}</div>
      </div>
      <div class="side">
        <div><strong>${escapeHtml(d.jurisdiction.code)}</strong> · ${escapeHtml(d.jurisdiction.name)}</div>
        <div>${escapeHtml(vehicle)}</div>
        <div class="muted">Stock ${escapeHtml(c.stock || "—")} · ${new Date().toLocaleDateString("en-US", { dateStyle: "medium" })}</div>
      </div>
    </div>
  `;
}

function signatureBlock(state) {
  const buyer = String(state.customer?.name || "").trim();
  const buyerLine = buyer ? `<br><small>${escapeHtml(buyer)}</small>` : "";
  return `
    <div class="sig-block">
      <div class="line">
        <div class="sig-space" aria-hidden="true"></div>
        <div class="sig-rule"></div>
        <div class="sig-label">Buyer Signature / Date${buyerLine}</div>
      </div>
      <div class="line">
        <div class="sig-space" aria-hidden="true"></div>
        <div class="sig-rule"></div>
        <div class="sig-label">Dealership Authorized Representative / Date</div>
      </div>
    </div>
  `;
}

function structureRows(state, d, opts = {}) {
  const o = normalizeOpts(opts);
  const s = state.structure;
  const tradeRow = o.showTradeCredit
    ? `<tr><th>Trade Credit</th><td>- ${formatMoney(s.trade)}</td></tr>`
    : "";
  const afRow = o.showAmountFinanced
    ? `<tr><th>Amount Financed</th><td><strong>${formatMoney(d.selectedAmountFinanced)}</strong></td></tr>`
    : "";
  const fcRow = o.showFinanceCharge
    ? `<tr><th>Finance Charge</th><td>${formatMoney(d.financeCharge)}</td></tr>`
    : "";
  return `
    <table class="lines">
      <tr><th>Price</th><td>${formatMoney(s.price)}</td></tr>
      <tr><th>Fees</th><td>${formatMoney(s.fees)}</td></tr>
      <tr><th>Insurance / GAP / VSI</th><td>${formatMoney(s.insGap)}</td></tr>
      <tr><th>Accessories</th><td>${formatMoney(s.accessories)}</td></tr>
      <tr><th>VSC / Products</th><td>${formatMoney(s.vsc)}</td></tr>
      <tr><th>Total Tax</th><td>${formatMoney(s.tax)}</td></tr>
      <tr><th>Down Payment</th><td>- ${formatMoney(d.selectedDown)}</td></tr>
      ${tradeRow}
      ${afRow}
      ${fcRow}
    </table>
  `;
}

function dealershipExtras(state, d, opts) {
  const o = normalizeOpts(opts);
  if (o.version !== "dealership") return "";
  const rows = [];
  if (o.showCost) {
    rows.push(
      `<tr><th>Vehicle Cost</th><td>${formatMoney(state.dealCosts.vehicleCost)}</td></tr>`
    );
    rows.push(
      `<tr><th>Fee Cost</th><td>${formatMoney(state.dealCosts.feeCost)}</td></tr>`
    );
    rows.push(
      `<tr><th>Product Cost</th><td>${formatMoney(state.dealCosts.productCost)}</td></tr>`
    );
    rows.push(
      `<tr><th>Deal Costs Total</th><td>${formatMoney(d.dealCostsTotal)}</td></tr>`
    );
  }
  if (o.showFrontGross) {
    rows.push(`<tr><th>Front Gross</th><td>${formatMoney(d.frontGross)}</td></tr>`);
  }
  if (o.showBackGross) {
    rows.push(`<tr><th>Back Gross</th><td>${formatMoney(d.backGross)}</td></tr>`);
  }
  if (o.showFrontGross || o.showBackGross) {
    rows.push(`<tr><th>Total Gross</th><td>${formatMoney(d.totalGross)}</td></tr>`);
  }
  if (o.showNetProfit) {
    rows.push(`<tr><th>Commissions</th><td>${formatMoney(state.commissions)}</td></tr>`);
    rows.push(`<tr><th>Net Profit</th><td><strong>${formatMoney(d.netProfit)}</strong></td></tr>`);
  }
  if (!rows.length) return "";
  return `
    <div class="dealer-only">
      <h3>Dealership Version Detail</h3>
      <table class="lines">${rows.join("")}</table>
    </div>
  `;
}

/** Signatures + disclosure note after content (in-flow — no flex pin / blank pages). */
function pageShell(mainHtml, state, noteHtml) {
  return `
    <div class="page-body">${mainHtml}</div>
    <div class="page-foot">
      ${signatureBlock(state)}
      <div class="note">${noteHtml}</div>
    </div>
  `;
}

function quoteNoteHtml(state, d) {
  const manualTax = taxManualPrintNote(state);
  return `${escapeHtml(d.jurisdiction.disclosureShort)} Estimates pending credit approval · ${escapeHtml(BRAND)}.${
    manualTax ? ` ${escapeHtml(manualTax)}` : ""
  }`;
}

function buildClassicQuoteMain(state, d, opts) {
  const versionLabel =
    opts.version === "dealership" ? "Dealership Version" : "Customer Version";
  const termApr = opts.showApr
    ? `${d.selectedTerm.months} mo · ${formatPercent(d.selectedTerm.apr)}`
    : `${d.selectedTerm.months} mo`;
  const structureBlock = opts.showDealStructure
    ? `<h3>Deal Structure</h3>${structureRows(state, d, opts)}`
    : "";
  return `
    <div class="quote-classic">
      ${headerBlock(state, d, `Payment Quote · ${versionLabel}`)}
      <h2>Payment Quote</h2>
      <div class="hero-pay">
        <div><span>Selected Payment</span><strong>${formatMoney(d.selectedPayment)}</strong></div>
        <div><span>Down Payment</span><strong>${formatMoney(d.selectedDown)}</strong></div>
        <div><span>Term${opts.showApr ? " / APR" : ""}</span><strong style="font-size:20px">${termApr}</strong></div>
      </div>
      ${structureBlock}
      <p class="muted">First payment in ${state.structure.firstPaymentDays} days${
        opts.version === "dealership"
          ? ` · Buy Rate ${formatPercent(state.structure.buyRate)} (internal)`
          : ""
      }</p>
      ${dealershipExtras(state, d, opts)}
    </div>
  `;
}

function buildGridQuoteMain(state, d, opts) {
  const versionLabel =
    opts.version === "dealership" ? "Dealership Version" : "Customer Version";
  const termApr = opts.showApr
    ? `${d.selectedTerm.months} mo @ ${formatPercent(d.selectedTerm.apr)}`
    : `${d.selectedTerm.months} mo`;
  // Compact structure (2-col feel via short table) — keep quote + sigs on one page
  const structureBlock = opts.showDealStructure
    ? `<h3>Deal Structure</h3>${structureRows(state, d, opts)}`
    : "";
  return `
    <div class="quote-grid">
      ${headerBlock(state, d, `Payment Quote · ${versionLabel}`)}
      <div class="select-bar">
        <span>Selected</span>
        <strong>${formatMoney(d.selectedPayment)}/mo</strong>
        <span>· Down ${formatMoney(d.selectedDown)}</span>
        <span>· ${escapeHtml(termApr)}</span>
        ${
          opts.showAmountFinanced
            ? `<span>· AF ${formatMoney(d.selectedAmountFinanced)}</span>`
            : ""
        }
      </div>
      ${paymentOptionsMatrixHtml(state, d, opts)}
      ${structureBlock}
      <p class="muted">First payment in ${state.structure.firstPaymentDays} days${
        opts.version === "dealership"
          ? ` · Buy Rate ${formatPercent(state.structure.buyRate)} (internal)`
          : ""
      }</p>
      ${dealershipExtras(state, d, opts)}
    </div>
  `;
}

function paymentOptionsMatrixHtml(state, d, opts) {
  const downs = state.downs || [];
  const terms = state.terms || [];
  const matrix = d.matrix || [];
  if (!downs.length || !terms.length) return "";
  const selDi = Number(state.selection?.downIndex) || 0;
  const selTi = Number(state.selection?.termIndex) || 0;
  const head = downs
    .map(
      (down) =>
        `<th>Down<br>${escapeHtml(formatMoney(down))}</th>`
    )
    .join("");
  const rows = terms
    .map((term, ti) => {
      const termLabel = opts.showApr
        ? `${term.months} mo<br>${escapeHtml(formatPercent(term.apr))}`
        : `${term.months} mo`;
      const cells = downs
        .map((_, di) => {
          const cell = matrix[di]?.[ti];
          const pay = cell ? formatMoney(cell.payment) : "—";
          const selected =
            di === selDi && ti === selTi ? " is-selected" : "";
          return `<td class="pay-cell${selected}">${escapeHtml(pay)}</td>`;
        })
        .join("");
      return `<tr><th class="term-cell" scope="row">${termLabel}</th>${cells}</tr>`;
    })
    .join("");
  return `
    <h3>Monthly Payment Options</h3>
    <table class="pay-matrix" aria-label="Monthly payment options by down payment and term">
      <thead>
        <tr>
          <th scope="col">Term / APR</th>
          ${head}
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

export function buildQuoteHtml(state, opts) {
  const o = normalizeOpts(opts);
  const d = derived(state);
  const versionLabel =
    o.version === "dealership" ? "Dealership Version" : "Customer Version";
  const main =
    o.template === "grid"
      ? buildGridQuoteMain(state, d, o)
      : buildClassicQuoteMain(state, d, o);
  return wrapDocument(
    `${BRAND} · Quote · ${versionLabel}`,
    pageShell(main, state, quoteNoteHtml(state, d))
  );
}

export function buildDealSummaryHtml(state, opts) {
  const o = normalizeOpts(opts);
  const d = derived(state);
  const versionLabel =
    o.version === "dealership" ? "Dealership Version" : "Customer Version";
  const tradeRow = o.showTradeCredit
    ? `<tr><th>Trade Credit</th><td>- ${formatMoney(state.structure.trade)}</td></tr>`
    : "";
  const aprRow = o.showApr
    ? `<tr><th>APR</th><td>${formatPercent(d.selectedTerm.apr)}</td></tr>`
    : "";
  const afHero = o.showAmountFinanced
    ? `<div><span>Amount Financed</span><strong>${formatMoney(d.selectedAmountFinanced)}</strong></div>`
    : "";
  const fcHero = o.showFinanceCharge
    ? `<div><span>Finance Charge</span><strong style="font-size:20px">${formatMoney(d.financeCharge)}</strong></div>`
    : "";
  const main = `
    ${headerBlock(state, d, `Deal Summary · ${versionLabel}`)}
    <h2>Deal Summary</h2>
    <div class="hero-pay">
      <div><span>Selected Payment</span><strong>${formatMoney(d.selectedPayment)}</strong></div>
      ${afHero}
      ${fcHero}
    </div>
    <table class="lines">
      <tr><th>Down Payment</th><td>- ${formatMoney(d.selectedDown)}</td></tr>
      <tr><th>Term</th><td>${d.selectedTerm.months} months</td></tr>
      ${aprRow}
      ${
        o.version === "dealership"
          ? `<tr><th>Buy Rate</th><td>${formatPercent(state.structure.buyRate)}</td></tr>`
          : ""
      }
      <tr><th>Price</th><td>${formatMoney(state.structure.price)}</td></tr>
      <tr><th>Fees, Tax &amp; Products</th><td>${formatMoney(
        state.structure.fees +
          state.structure.tax +
          state.structure.insGap +
          state.structure.vsc +
          state.structure.accessories
      )}</td></tr>
      ${tradeRow}
      <tr><th>First Payment (days)</th><td>${state.structure.firstPaymentDays ?? 30}</td></tr>
    </table>
    ${dealershipExtras(state, d, o)}
  `;
  const manualTax = taxManualPrintNote(state);
  const note = `${escapeHtml(d.jurisdiction.disclosureShort)} Deal summary prepared with ${escapeHtml(BRAND)}.${
    manualTax ? ` ${escapeHtml(manualTax)}` : ""
  }`;
  return wrapDocument(
    `${BRAND} · Deal Summary · ${versionLabel}`,
    pageShell(main, state, note)
  );
}

export function openPrintWindow(html, title = BRAND) {
  return openHtmlDocument(html, { title });
}

/** Preferred quote print path: HTML window (mobile-friendly). */
export function openQuoteHtml(state, opts) {
  return openPrintWindow(buildQuoteHtml(state, opts), `${BRAND} · Payment Quote`);
}

export function openQuotePdf(state, opts) {
  // Prefer HTML print for reliability (especially iPhone/iPad).
  return openQuoteHtml(state, opts);
}

export function openDealSummaryPdf(state, opts) {
  // Use the dedicated summary HTML layout (not a retitled quote PDF)
  return openHtmlDocument(buildDealSummaryHtml(state, opts), {
    title: `${BRAND} · Deal Summary`,
  });
}

/** Compact HTML used for modal thumbnail preview. */
export function previewSnippet(state, kind, opts) {
  const o = normalizeOpts(opts);
  const d = derived(state);
  const version =
    o.version === "dealership" ? "Dealership Version" : "Customer Version";
  const title = kind === "summary" ? "Deal Summary" : "Payment Quote";
  const dealerBits =
    o.version === "dealership"
      ? [
          o.showCost ? `Costs ${formatMoney(d.dealCostsTotal)}` : null,
          o.showFrontGross ? `Front ${formatMoney(d.frontGross)}` : null,
          o.showBackGross ? `Back ${formatMoney(d.backGross)}` : null,
          o.showNetProfit ? `Net ${formatMoney(d.netProfit)}` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "Customer-facing totals only";

  const aprBit = o.showApr ? ` · ${formatPercent(d.selectedTerm.apr)}` : "";
  const tradeBit = o.showTradeCredit
    ? ` · Trade ${formatMoney(state.structure.trade)}`
    : "";
  const afBit = o.showAmountFinanced
    ? `AF ${formatMoney(d.selectedAmountFinanced)}`
    : null;
  const fcBit = o.showFinanceCharge
    ? `FC ${formatMoney(d.financeCharge)}`
    : null;
  const midLine = [afBit, fcBit].filter(Boolean).join(" · ");
  const templateHint =
    kind !== "summary" && o.template === "grid" ? " · Grid layout" : "";

  return `
    <div class="preview-sheet${o.template === "grid" && kind !== "summary" ? " is-grid" : ""}">
      <div class="preview-brand">ENVISION</div>
      <div class="preview-sub">${title} · ${version}${templateHint}</div>
      <div class="preview-pay">${formatMoney(d.selectedPayment)}<span>/mo</span></div>
      <div class="preview-line">${d.selectedTerm.months} mo${aprBit} · Down ${formatMoney(d.selectedDown)}${tradeBit}</div>
      ${midLine ? `<div class="preview-line">${midLine}</div>` : ""}
      <div class="preview-line muted">${d.jurisdiction.code} · ${dealerBits}</div>
      <div class="preview-line muted">${BRAND}</div>
    </div>
  `;
}
